/**
 * Centralized Error Handling Utility
 *
 * This module provides user-friendly error messages while ensuring
 * technical details are logged to Sentry for debugging.
 *
 * NEVER expose technical error messages (URLs, stack traces, AWS details) to users.
 */

import { captureError } from './sentry';
import i18n from '@/i18n';

/**
 * Error categories for user-friendly messages
 */
export type ErrorCategory =
  | 'network'
  | 'auth'
  | 'validation'
  | 'notFound'
  | 'permission'
  | 'payment'
  | 'upload'
  | 'server'
  | 'unknown';

/**
 * User-friendly error messages by category.
 * Functions so translations are evaluated at runtime, not at import time.
 */
const USER_FRIENDLY_MESSAGES: Record<ErrorCategory, () => string> = {
  network: () => i18n.t('errors.network'),
  auth: () => i18n.t('errors.auth'),
  validation: () => i18n.t('errors.validation'),
  notFound: () => i18n.t('errors.notFound'),
  permission: () => i18n.t('errors.permission'),
  payment: () => i18n.t('errors.payment'),
  upload: () => i18n.t('errors.upload'),
  server: () => i18n.t('errors.server'),
  unknown: () => i18n.t('errors.unknown'),
};

/**
 * Patterns to detect error categories from error messages
 */
const ERROR_PATTERNS: Array<{ pattern: RegExp; category: ErrorCategory }> = [
  // Network errors
  { pattern: /failed to fetch/i, category: 'network' },
  { pattern: /network/i, category: 'network' },
  { pattern: /net::ERR_/i, category: 'network' },
  { pattern: /CORS/i, category: 'network' },
  { pattern: /timeout/i, category: 'network' },
  { pattern: /connection refused/i, category: 'network' },
  { pattern: /offline/i, category: 'network' },

  // Auth errors
  { pattern: /unauthorized/i, category: 'auth' },
  { pattern: /not authenticated/i, category: 'auth' },
  { pattern: /UserUnAuthenticatedException/i, category: 'auth' },
  { pattern: /session expired/i, category: 'auth' },
  { pattern: /invalid.*token/i, category: 'auth' },
  { pattern: /UserAlreadyAuthenticatedException/i, category: 'auth' },

  // Permission errors
  { pattern: /forbidden/i, category: 'permission' },
  { pattern: /access denied/i, category: 'permission' },
  { pattern: /not authorized/i, category: 'permission' },

  // Not found errors
  { pattern: /not found/i, category: 'notFound' },
  { pattern: /404/i, category: 'notFound' },

  // Validation errors
  { pattern: /validation/i, category: 'validation' },
  { pattern: /invalid/i, category: 'validation' },
  { pattern: /required/i, category: 'validation' },

  // Payment errors
  { pattern: /payment/i, category: 'payment' },
  { pattern: /card/i, category: 'payment' },
  { pattern: /stripe/i, category: 'payment' },
  { pattern: /declined/i, category: 'payment' },

  // Upload errors
  { pattern: /upload/i, category: 'upload' },
  { pattern: /file.*too.*large/i, category: 'upload' },
  { pattern: /presigned/i, category: 'upload' },
  { pattern: /s3/i, category: 'upload' },

  // Server errors
  { pattern: /500/i, category: 'server' },
  { pattern: /502/i, category: 'server' },
  { pattern: /503/i, category: 'server' },
  { pattern: /internal server/i, category: 'server' },
];

/**
 * Patterns that indicate a message contains sensitive/technical information
 * that should never be shown to users
 */
const SENSITIVE_PATTERNS = [
  /execute-api/i,          // AWS API Gateway URLs
  /amazonaws\.com/i,       // Any AWS URLs
  /s3\./i,                 // S3 URLs
  /dynamodb/i,             // DynamoDB references
  /lambda/i,               // Lambda references
  /cognito/i,              // Cognito references (unless user-friendly)
  /arn:/i,                 // AWS ARNs
  /https?:\/\/[^\s]+/i,    // Any URL
  /\b[a-z0-9]{20,}\b/i,    // Long hex/alphanumeric strings (tokens, IDs)
  /stack trace/i,          // Stack traces
  /at \w+\.\w+/i,          // Stack trace patterns
  /Error:/i,               // Error prefixes
  /Exception/i,            // Exception types
];

/**
 * Check if an error message contains sensitive information
 */
function containsSensitiveInfo(message: string): boolean {
  return SENSITIVE_PATTERNS.some(pattern => pattern.test(message));
}

/**
 * Detect the error category from an error
 */
function detectErrorCategory(error: unknown): ErrorCategory {
  const message = error instanceof Error ? error.message : String(error);

  for (const { pattern, category } of ERROR_PATTERNS) {
    if (pattern.test(message)) {
      return category;
    }
  }

  return 'unknown';
}

/**
 * Get a user-friendly error message from any error.
 *
 * This function:
 * 1. Logs the technical error to Sentry
 * 2. Returns a safe, user-friendly message
 *
 * @param error - The error to handle
 * @param context - Optional context for Sentry logging
 * @param fallbackMessage - Optional custom fallback message
 * @returns A user-friendly error message safe to display
 */
export function getUserFriendlyErrorMessage(
  error: unknown,
  context?: Record<string, unknown>,
  fallbackMessage?: string
): string {
  // Log the full error to Sentry with context
  const errorObj = error instanceof Error ? error : new Error(String(error));
  captureError(errorObj, {
    ...context,
    originalMessage: errorObj.message,
    errorType: errorObj.name,
  });

  // If it's already a user-friendly message (no sensitive info), we might use it
  const message = errorObj.message;
  if (message && !containsSensitiveInfo(message) && message.length < 100) {
    // Still prefer category-based messages for consistency
    const category = detectErrorCategory(error);
    if (category !== 'unknown') {
      return USER_FRIENDLY_MESSAGES[category]();
    }
    // For unknown category with safe message, return the message
    return message;
  }

  // Detect category and return appropriate message
  const category = detectErrorCategory(error);
  return fallbackMessage || USER_FRIENDLY_MESSAGES[category]();
}

/**
 * Specific error handlers for common operations
 */
export const errorMessages = {
  // Templates
  loadTemplates: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'loadTemplates' }, i18n.t('errors.loadTemplates')),

  saveTemplate: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'saveTemplate' }, i18n.t('errors.saveTemplate')),

  deleteTemplate: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'deleteTemplate' }, i18n.t('errors.deleteTemplate')),

  addTemplate: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'addTemplate' }, i18n.t('errors.addTemplate')),

  // Authentication
  login: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'login' }, i18n.t('errors.login')),

  signup: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'signup' }, i18n.t('errors.signup')),

  // Upload
  upload: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'upload' }, i18n.t('errors.uploadFailed')),

  // Documents/Jobs
  submitJob: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'submitJob' }, i18n.t('errors.submitJob')),

  loadDocuments: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'loadDocuments' }, i18n.t('errors.loadDocuments')),

  // Projects
  loadProjects: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'loadProjects' }, i18n.t('errors.loadProjects')),

  saveProject: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'saveProject' }, i18n.t('errors.saveProject')),

  deleteProject: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'deleteProject' }, i18n.t('errors.deleteProject')),

  // Admin
  loadUsers: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'loadUsers' }, i18n.t('errors.loadUsers')),

  loadDefaultTemplates: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'loadDefaultTemplates' }, i18n.t('errors.loadDefaultTemplates')),

  saveDefaultTemplate: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'saveDefaultTemplate' }, i18n.t('errors.saveDefaultTemplate')),

  deleteDefaultTemplate: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'deleteDefaultTemplate' }, i18n.t('errors.deleteDefaultTemplate')),

  // System Prompts
  loadSystemPrompts: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'loadSystemPrompts' }, i18n.t('errors.loadSystemPrompts')),

  saveSystemPrompt: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'saveSystemPrompt' }, i18n.t('errors.saveSystemPrompt')),

  deleteSystemPrompt: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'deleteSystemPrompt' }, i18n.t('errors.deleteSystemPrompt')),

  // Account
  deleteAccount: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'deleteAccount' }, i18n.t('errors.deleteAccount')),

  adminDeleteUser: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'adminDeleteUser' }, i18n.t('errors.adminDeleteUser')),

  adminResetQuota: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'adminResetQuota' }, i18n.t('errors.adminResetQuota')),

  refineDocument: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'refineDocument' }, i18n.t('documentEditor.aiRefine.error')),

  getTranscription: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'getTranscription' }, i18n.t('errors.getTranscription')),

  updateAccount: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'updateAccount' }, i18n.t('errors.updateAccount')),

  // Payment
  payment: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'payment' }, i18n.t('errors.paymentFailed')),

  // Sync
  sync: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'sync' }, i18n.t('errors.sync')),

  // Avatar
  uploadAvatar: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'uploadAvatar' }, i18n.t('errors.uploadAvatar')),

  // Signup
  registration: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'registration' }, i18n.t('errors.registration')),

  verification: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'verification' }, i18n.t('errors.verification')),

  resendCode: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'resendCode' }, i18n.t('errors.resendCode')),

  // Generic
  generic: (error: unknown) =>
    getUserFriendlyErrorMessage(error, { operation: 'unknown' }),
};
